import { BrandCompany, GroupedProduct, Product } from '../../../../../models/product/types';
import { Division, Section } from '../../../../../models/productDivision/types';

// A flattened, virtualization-friendly representation of the division tree.
// Only rows that are currently visible (expanded) are produced, so the
// react-virtualized List renders/measures just what the user can see.
export interface DivisionHeaderRowData {
    type: 'division';
    key: string;
    division: Division<Product>;
}

export interface SectionHeaderRowData {
    type: 'section';
    key: string;
    division: Division<Product>;
    section: Section<Product>;
    lastSection: boolean;
}

export interface GroupHeaderRowData {
    type: 'group';
    key: string;
    group: GroupedProduct<Product>;
    groupIndex: number;
}

export interface ProductRowData {
    type: 'product';
    key: string;
    product: Product;
    previousProduct?: Product;
    divider: boolean;
    brandInfo?: BrandCompany;
    lastProduct: boolean;
    previousProductManuallyAdded: boolean;
    manuallyAddedProductsByBrand?: Product[];
}

export type VirtualRow =
    | DivisionHeaderRowData
    | SectionHeaderRowData
    | GroupHeaderRowData
    | ProductRowData;

const getLowestLevelNumber = (product: Product): string =>
    product.mfSection?.number ||
    product?.mfSectionLevel3?.number ||
    product?.mfSectionLevel2?.number ||
    product?.mfSectionLevel1?.number ||
    '';

const getLowestLevelLabel = (product: Product): string =>
    product.mfSection?.label ||
    product.mfSectionLevel3?.label ||
    product.mfSectionLevel2?.label ||
    product.mfSectionLevel1?.label ||
    '';

function sortByKey<T>(array: Array<T>, key: keyof T): Array<T> {
    return array.sort((a, b) => {
        let x: unknown = a[key];
        let y: unknown = b[key];

        if (typeof x === 'string') {
            x = ('' + x).toLowerCase();
        }
        if (typeof y === 'string') {
            y = ('' + y).toLowerCase();
        }

        if ((x as never) < (y as never)) {
            return -1;
        }
        if ((x as never) > (y as never)) {
            return 1;
        }
        return 0;
    });
}

// Grouping + sorting is O(n log n). Cache it by the products array reference so
// it is only recomputed when a section's products actually change (the parent
// replaces the products array immutably on edit/add/delete).
const groupCache = new WeakMap<Array<Product>, Array<GroupedProduct<Product>>>();

export function groupSectionProducts(
    products: Array<Product>,
): Array<GroupedProduct<Product>> {
    const cached = groupCache.get(products);
    if (cached) {
        return cached;
    }

    const groupedMap = products.reduce((entryMap, product) => {
        const number = getLowestLevelNumber(product);
        return entryMap.set(number, [...(entryMap.get(number) || []), product]);
    }, new Map<string, Array<Product>>());

    const groups: Array<GroupedProduct<Product>> = [];
    for (const [number, value] of groupedMap.entries()) {
        groups.push({
            products: value,
            number,
            label: getLowestLevelLabel(value[0]),
        });
    }

    const sortedGroups = sortByKey(groups, 'number');
    for (const group of sortedGroups) {
        group.products = sortByKey(group.products, 'productName');
        group.products = sortByKey(group.products, 'type');
        group.products = sortByKey(group.products, 'company');
    }

    groupCache.set(products, sortedGroups);
    return sortedGroups;
}

function getBrandInfo(
    product: Product,
    brandsInfo: Array<BrandCompany>,
): BrandCompany | undefined {
    return brandsInfo.find((b) => b.id === product.companyID);
}

function isProductManuallyAdded(
    product: Product,
    brandInfo: BrandCompany | undefined,
): boolean {
    if (product && brandInfo) {
        return product.type === 'userProducts' && brandInfo?.type === 'companies';
    }
    return false;
}

function getManuallyAddedProductsByBrand(
    products: Array<Product>,
    companyName: string,
    brandsInfo: Array<BrandCompany>,
): Array<Product> {
    return products.filter((product) => {
        const brandInfo = getBrandInfo(product, brandsInfo);
        return isProductManuallyAdded(product, brandInfo) && product.company === companyName;
    });
}

function getDividerValue(
    nextProduct: Product | undefined,
    currentProduct: Product,
    currentProductBrandInfo: BrandCompany | undefined,
    brandsInfo: Array<BrandCompany>,
): boolean {
    let dividerValue = false;
    if (nextProduct) {
        const nextProductBrandInfo = getBrandInfo(nextProduct, brandsInfo);

        if (currentProduct.company !== nextProduct.company) {
            dividerValue = true;
        }

        if (isProductManuallyAdded(nextProduct, nextProductBrandInfo)) {
            dividerValue = true;

            if (
                isProductManuallyAdded(currentProduct, currentProductBrandInfo) &&
                currentProduct.company === nextProduct.company
            ) {
                dividerValue = false;
            }
        }
    }
    return dividerValue;
}

// Flatten the (visible part of the) division tree into a single row array.
export function buildVirtualRows(
    divisions: Array<Division<Product>>,
    brandsInfo: Array<BrandCompany>,
): Array<VirtualRow> {
    const rows: Array<VirtualRow> = [];

    divisions.forEach((division) => {
        rows.push({ type: 'division', key: `d:${division.id}`, division });
        if (!division.clicked) {
            return;
        }

        division.sections.forEach((section, sectionIndex) => {
            const lastSection = sectionIndex + 1 === division.sections.length;
            rows.push({
                type: 'section',
                key: `s:${division.id}:${section.id}`,
                division,
                section,
                lastSection,
            });
            if (!section.clicked) {
                return;
            }

            const groups = groupSectionProducts(section.products);
            groups.forEach((group, groupIndex) => {
                rows.push({
                    type: 'group',
                    key: `g:${division.id}:${section.id}:${group.number}`,
                    group,
                    groupIndex,
                });

                group.products.forEach((product, index, products) => {
                    const currentProductBrandInfo = getBrandInfo(product, brandsInfo);

                    let previousProductManuallyAdded = false;
                    let manuallyAddedProductsByBrand: Array<Product> | undefined;

                    if (index - 1 >= 0) {
                        const previousBrandInfo = getBrandInfo(products[index - 1], brandsInfo);
                        if (previousBrandInfo) {
                            previousProductManuallyAdded = isProductManuallyAdded(
                                products[index - 1],
                                previousBrandInfo,
                            );
                        }
                    }

                    if (
                        !previousProductManuallyAdded &&
                        isProductManuallyAdded(product, currentProductBrandInfo)
                    ) {
                        manuallyAddedProductsByBrand = getManuallyAddedProductsByBrand(
                            products,
                            product.company,
                            brandsInfo,
                        );
                    }

                    const divider = getDividerValue(
                        products[index + 1],
                        product,
                        currentProductBrandInfo,
                        brandsInfo,
                    );

                    const lastProduct =
                        groupIndex + 1 === groups.length &&
                        index + 1 === group.products.length &&
                        lastSection;

                    rows.push({
                        type: 'product',
                        key: `p:${product.projectProductId ?? product.id}`,
                        product,
                        previousProduct: index ? products[index - 1] : undefined,
                        divider,
                        brandInfo: currentProductBrandInfo,
                        lastProduct,
                        previousProductManuallyAdded,
                        manuallyAddedProductsByBrand,
                    });
                });
            });
        });
    });

    return rows;
}
